/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.32.0
 * @since 1.19.0
 */

import { useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@/main/modules/logging';
import type { IOptionItemProps } from '@components/common/Select/Select';
import { appHistoryPush } from '@modules/app/appHistory';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import PersonalDataDeleteModal from '@modules/personalDataDelete/components/PersonalDataDeleteModal';
import { makeSelectGuardComponents } from '@modules/role';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { openExternalRoute } from '@routes/constants/externalRoutes';
import { RoutePath } from '@routes/constants/routesConstants';

import {
    getWizardImportPersonalDataUrl,
    isWizardImportPersonalDataExternalUrl,
} from '../../WizardImportPersonalData/utils/getWizardImportPersonalDataUrl';
import { EPageSubscriberConstants } from '../pageSubscriberConstants';


export const useTableSubscriberPersonalImport = () => {

    const dispatch = useDispatch();

    const componentsFeature = useSelector(makeSelectGuardComponents());

    const personalImportSelector = useMemo(
        () => makeSelectGuardComponentEnabled(AccessComponentKeyEnum.endUserDeleteBulk), []);
    const isPersonalImportEnabled = useSelector(personalImportSelector);


    const openPersonalDataImport = () => {
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.IMPORT_PERSONAL_DATA }));

        const url = getWizardImportPersonalDataUrl(componentsFeature);
        if (isWizardImportPersonalDataExternalUrl(componentsFeature)) {
            openExternalRoute(url);
        } else {
            appHistoryPush(url);
        }
    };

    const openPersonalDataDelete = () => {
        dispatch(modalOpen({
            render: (props: IModalRenderProps) => <PersonalDataDeleteModal onCloseModal={props.onCloseModal} />,
        }));
    };

    const openWizardMultiSearch = () => {
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.MULTI_SEARCH }));
        appHistoryPush(RoutePath.wizardMultiSearch);
    };

    const openWizardImportSubscribersImport = () => {
        dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.IMPORT_SUBSCRIBERS_LIMITS }));
        dispatch(logBusinessEvent(
            {
                eventName: ELogBusinessEventName.USER_IMPORT_LIMIT_MASTER_STEP_1, 
                type: ELogBusinessEventType.common,
            },
        ));
        appHistoryPush(RoutePath.wizardImportSubscribersLimits);
    }

    const openPersonalData = (item?: IOptionItemProps) => {
        if (!item || item.value === EPageSubscriberConstants.import) {
            openPersonalDataImport();
        } else if (item.value === EPageSubscriberConstants.delete) {
            openPersonalDataDelete();
        } else if (item.value === EPageSubscriberConstants.wizardMultiSearch) {
            openWizardMultiSearch();
        } else if (item.value === EPageSubscriberConstants.wizardImportSubscribersLimits) {
            openWizardImportSubscribersImport();
        }
    };

    return {
        isPersonalImportEnabled,
        openPersonalData,
    }
};
